import { RouterContext, RouterParentState } from '@brainfocus/web/router/router_context';
import { resolvePathname } from '@brainfocus/web/router/url_utils';
import { usePathname } from '@brainfocus/web/router/use_pathname';
import { LoaderFunction } from '@brainfocus/web/router/loader_manager';
import { useLoaderManager } from '@brainfocus/web/router/loader_context';
import { setGlobalRoutes } from '@brainfocus/web/router/use_navigate';
import { findMatchingRoute } from '@brainfocus/web/router/route_utils';
import { VNode } from 'preact';
import { Suspense } from 'preact/compat';
import { useContext, useMemo, useEffect, useRef } from 'preact/hooks';
import { useEventCallback } from '@brainfocus/web/utils/react/use_event_callback';

export function Router({
    routes,
}: {
    routes: RouterRoute[];
}) {
    const pathname = usePathname();
    const routerContext = useContext(RouterContext);
    const loaderManager = useLoaderManager();

    // Set global routes for use_navigate
    useEffect(() => {
        setGlobalRoutes(routes, routerContext.pathPrefix);
    }, [routes, routerContext.pathPrefix]);

    // The route is resolved again on a pathname change only, with the routes and parent
    // context of that render: `routes` can be a new array each render (`Manage`), and
    // resolving runs guards that may redirect.
    const latest = useRef({ routes, routerContext });
    latest.current = { routes, routerContext };
    const currentRoute = useMemo(
        () => findCurrentRoute(latest.current.routes, latest.current.routerContext, pathname),
        [pathname],
    );
    const matchRoute = useEventCallback((path: string) => findMatchingRoute(routes, routerContext, path));

    // Execute loader on mount if route has one and data not cached
    // Also re-execute when cache is invalidated
    useEffect(() => {
        const match = matchRoute(pathname);

        const checkAndLoad = () => {
            if (match && match.route.loader) {
                // Check if data already cached
                const cached = loaderManager.getCachedData(match.route.path, match.params);
                if (!cached) {
                    // Execute loader if not cached
                    loaderManager.loadRoute(match.route.path, match.route.loader, match.params);
                }
            }
        };

        // Execute immediately when pathname changes
        checkAndLoad();

        // Also subscribe to cache updates - re-execute loader if THIS route's entry
        // was invalidated. An entry that merely expired is not refetched here: any
        // unrelated cache event (an annotation save invalidating the analysis
        // payload) used to refetch the mounted route after the TTL and rebuild its
        // view (#1091). Expiry still applies on mount / navigation above.
        const subscription = loaderManager.getCacheUpdates$().subscribe(() => {
            if (match && match.route.loader && !loaderManager.hasEntry(match.route.path, match.params)) {
                loaderManager.loadRoute(match.route.path, match.route.loader, match.params);
            }
        });

        return () => subscription.unsubscribe();
    }, [pathname, loaderManager, matchRoute]);

    return (
        <RouterContext.Provider value={{ pathPrefix: currentRoute?.path ?? routerContext.pathPrefix }}>
            {/* A lazy page (#1169) suspends here until its chunk arrives; a loader that preloads it never does. */}
            <Suspense fallback={null}>{currentRoute?.component}</Suspense>
        </RouterContext.Provider>
    );
}

function findCurrentRoute(
    routes: RouterRoute[],
    context: RouterParentState,
    pathname: string,
): ResolvedRoute | undefined {
    for (const r of routes) {
        if (!pathname.endsWith('/')) {
            pathname = pathname + '/';
        }
        let resolvedPath = resolvePathname(context.pathPrefix, r.path);
        if (resolvedPath.match(/[^/]\$$/)) {
            resolvedPath = resolvedPath.replace('$', '/$');
        } else if (resolvedPath.match(/[^/$]$/)) {
            resolvedPath = resolvedPath + '/';
        }
        const wildSub = resolvedPath.replaceAll('/', '\\/').replace(/:([a-zA-Z0-9]+)/g, '([a-zA-Z0-9\\-._]+)');
        const expStr = `^${wildSub}`;
        const exp = new RegExp(expStr);
        const match = pathname.match(exp);
        if (match) {
            const guards = r.guard && r.guard instanceof Array
                ? r.guard
                : r.guard
                    ? [r.guard]
                    : [];
            const failingGuard = guards.map(g => g()).find(result => result !== true);
            if (failingGuard === undefined) {
                return {
                    component: r.factory(...match.slice(1)),
                    path: match[0],
                };
            } else if (typeof failingGuard === 'object') {
                if (failingGuard.redirect !== pathname) {
                    history.replaceState({}, '', resolvePathname(context.pathPrefix, failingGuard.redirect));
                    // Execute side effect callback once before recursive call
                    if (r.onGuardRedirect) {
                        r.onGuardRedirect(failingGuard.redirect);
                    }
                    return findCurrentRoute(routes, context, failingGuard.redirect);
                }
            }
        }
    }
    return undefined;
}

export interface RouterRoute {
    path: string;
    /**
     * When specified, the guard needs to return true to be able to visiet that
     * route. If multiple guards are specified, all need to pass.
     */
    guard?: RouteGuard | RouteGuard[];
    /**
     * Loader function that executes BEFORE navigation completes.
     * Navigation is blocked until the loader resolves.
     * Use this to load data required by the route.
     */
    loader?: LoaderFunction;
    /**
     * Callback executed once when a guard triggers a redirect.
     * Use this for side effects like showing flash messages.
     * Guards should remain pure functions - put side effects here instead.
     */
    onGuardRedirect?: (redirectPath: string) => void;
    factory: (...params: string[]) => VNode;
}

export type RouteGuard = () => boolean | { redirect: string };

interface ResolvedRoute {
    component: VNode;
    path: string;
}
